import { useMemo } from 'react'

import { collideByRadius } from './collideByRadius'
import { PILL_CSS_HEIGHT } from './generators/generatePillIcon'

// Under this count the render cost is already cheap; leave what renders alone.
const MIN_ITEMS_TO_CULL = 200
// A zoomed-out route is a shape, not a set of readable pills. Thinning it to a
// handful loses the shape, so keep at least this many and accept the overlap.
export const MIN_VISIBLE_MARKERS = 300
const MIN_RADIUS_PX = 2

interface UseMarkerDensityParams<TItem, TId> {
  items: TItem[]
  zoom?: number
  getId: (item: TItem) => TId
  getPosition: (item: TItem) => readonly [number, number] | null | undefined
  keepIds?: ReadonlySet<TId> | null
  radiusPx?: number
  offsetY?: number
}

/**
 * Drops stop markers that are physically stacked on top of each other at the
 * current zoom, so a dense route reads as distinct pills and the DOM path mounts
 * fewer nodes. The set depends on zoom alone — panning never changes it — and it
 * only grows as you zoom in, so no marker is ever out of reach.
 */
export function useMarkerDensity<TItem, TId>({
  items,
  zoom,
  getId,
  getPosition,
  keepIds = null,
  // A pill is ~35px tall and wider than that, so its height approximates the
  // "these are touching" distance without measuring each label.
  radiusPx = PILL_CSS_HEIGHT,
  offsetY = 0,
}: UseMarkerDensityParams<TItem, TId>): TItem[] {
  return useMemo(() => {
    if (!items || items.length < MIN_ITEMS_TO_CULL) return items
    if (typeof zoom !== 'number' || !Number.isFinite(zoom)) return items

    const floor = Math.min(MIN_VISIBLE_MARKERS, items.length)
    let radius = radiusPx
    let visibleIds = collideByRadius({ items, zoom, radiusPx: radius, offsetY, getId, getPosition, keepIds })
    // Far enough out, the whole route compresses into a few pill-widths and a
    // fixed radius would leave a handful of pills. Relax until the route reads.
    while (visibleIds.size < floor && radius > MIN_RADIUS_PX) {
      radius /= 2
      visibleIds = collideByRadius({ items, zoom, radiusPx: radius, offsetY, getId, getPosition, keepIds })
    }

    if (visibleIds.size >= items.length) return items
    return items.filter(item => visibleIds.has(getId(item)))
  }, [items, zoom, radiusPx, offsetY, getId, getPosition, keepIds])
}
